<template>
  <section class="block">
    <table class="table table-striped table-hover caption-top">
      <caption>
        <nav>
          <label style="margin-top: 12px;">
            <font-awesome-icon icon="search" />
            <input type="text" placeholder="検索フィルター" v-model="filterString" />
          </label>
          <button @click="newItem" v-if="newUri !== ''">
            <span>{{ newCaption }}</span>
            <font-awesome-icon icon="plus-circle" />
          </button>
        </nav>
      </caption>
      <thead v-if="data.length">
        <tr>
          <th scope="col" v-for="(item, index) in headers" :key="index" @click="sortBy(index)">
            <div>
              <span>{{ item }}</span>
              <nav v-if="item !== ''">
                <font-awesome-icon icon="sort-up" :class="up[index]" />
                <font-awesome-icon icon="sort-down" :class="down[index]" />
              </nav>
            </div>
          </th>
        </tr>
      </thead>
      <tbody v-if="data?.length">
        <tr v-for="(row, idx1) in sortData" :key="idx1">
          <td scope="row" v-for="(item, idx2) in row" :key="idx2">
            <a v-if="item.link" :href="item.link">{{ item.data }}</a>
            <button v-else-if="item.button" @click="emitBtnEvnt(item.button)">{{ item.data }}</button>
            <span v-else>{{ item.data }}</span>
            <span style="float: right;" v-if="item.organization">
              <span>{{  item.organization }}</span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import router from '@/router';

enum SortType {
  none,
  asc,
  desc,
}

interface SortedData {
  data: string;
  link: string | undefined;
  button: string | undefined;
  organization?: string;
}

export default defineComponent({
  name: 'Table',
  props: {
    headers: {
      type: Array,
      require: true,
    },
    data: {
      type: Array,
      require: true,
    },
    linkCols: Array,
    buttonCols: Array,
    organization: {
      type: Array,
      default: () => [],
    },
    newUri: {
      type: String,
      default: '',
    },
    newCaption: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      filterString: '',
      sortState: SortType.none as SortType,
      sortIdx: -1,
      up: [] as string[],
      down: [] as string[],
    };
  },
  computed: {
    sortData(): SortedData[][] {
      this.sortIdx < 0 && this.sortBy(0);

      const list: SortedData[][] = [];
      const sesame = this.sortState === SortType.asc ? 1 : -1;
      const q = this.filterString.toLowerCase().trim();
      const data = this.data as string[][];
      const link = this.linkCols as string[][];
      const button = this.buttonCols as string[][];

      for (let n = 0; n < data.length; n += 1) {
        if (data[n].filter((a) => a.toLowerCase().includes(q)).length <= 0) {
          continue;
        }
        const rows: SortedData[] = [];
        for (let i = 0; i < data[n].length; i += 1) {
          rows.push({
            organization: this.organization[n] as string,
            data: data[n][i],
            link: link && link[n] ? link[n][i] : undefined,
            button: button && button[n] ? button[n][i] : undefined,
          });
        }
        list.push(rows);
      }
      if (this.sortIdx < 0 || this.sortState === SortType.none) {
        return list;
      }

      return list.sort((a, b) => {
        if (a[this.sortIdx].data < b[this.sortIdx].data) return -1 * sesame;
        if (a[this.sortIdx].data > b[this.sortIdx].data) return 1 * sesame;
        return 0;
      });
    },
  },
  methods: {
    emitBtnEvnt(param: string) {
      const params = param.split('|');
      this.$emit(params[0], params[1]);
    },
    sortBy(index: number) {
      const length = this.headers?.length ?? 0;
      for (let n = 0; n < length; n += 1) {
        if (n !== index) {
          this.up[n] = this.down[n] = '';
          continue;
        }
        if (n !== this.sortIdx) {
          this.sortIdx = n;
          this.sortState = SortType.asc;
          this.up[n] = 'selected';
          this.down[n] = '';
        } else if (this.sortState === SortType.asc) {
          this.sortState = SortType.desc;
          this.up[n] = '';
          this.down[n] = 'selected';
        } else {
          this.sortState = SortType.asc;
          this.up[n] = 'selected';
          this.down[n] = '';
        }
      }
    },
    newItem() {
      if (this.newUri) router.push(this.newUri);
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/assets/const.scss' as *;

.block {
  @media (min-width: $desktop) {
    padding: 0 40px;
  }

  caption {
    padding: 0 0 20px;
    color: inherit;

    nav {
      display: flex;
      align-items: center;
      padding: 0 0.5rem;

      @media (min-width: $tablet) {
        padding: 0 20px;
      }

      @media (min-width: $desktop) {
        padding: 0;
      }
    }

    label {
      position: relative;
      border: 1px solid var(--bs-gray-400);
      border-radius: 5px;

      svg {
        position: absolute;
        top: calc(50% - 0.5rem);
        left: 10px;
        color: var(--bs-gray-400);
      }

      input {
        display: block;
        width: 240px;
        padding: 5px 5px 5px calc(1rem + 20px);
      }
    }

    button {
      margin-left: auto;
      padding: 0 5px;

      span {
        order: -1;
        display: none;
        margin-right: 5px;

        @media (min-width: $tablet) {
          display: unset;
        }
      }
    }
  }

  .caption-top {
    caption {
      caption-side: top !important;
    }
  }


  thead {
    border: none;
    background-color: var(--bs-gray-800);
    color: var(--bs-gray-200);

    th {
      border: none;
      font-weight: normal;
    }

    div {
      display: flex;
      align-items: center;
    }

    nav {
      position: relative;
      width: 1rem;
      height: 1rem;
      margin-left: 5px;
    }

    svg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      color: var(--bs-gray-700);

      &.selected {
        color: var(--bs-info);
      }
    }
  }

  tbody {
    border: none;

    td {
      border: none;
    }

    a {
      color: var(--bs-primary);
      text-decoration: none;
    }
  }
}
</style>